'use client';

import * as React from 'react';
import {
  CarouselLayout,
  ChatCloseIcon,
  ChatEntry,
  ChatIcon,
  ChatToggle,
  ConnectionStateToast,
  FocusLayout,
  FocusLayoutContainer,
  GearIcon,
  GridLayout,
  isTrackReference,
  LayoutContextProvider,
  LeaveIcon,
  MediaDeviceMenu,
  ParticipantTile,
  RoomAudioRenderer,
  StartMediaButton,
  TrackReferenceOrPlaceholder,
  TrackToggle,
  useChat,
  useCreateLayoutContext,
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  usePinnedTracks,
  usePersistentUserChoices,
  useRoomContext,
  useTracks,
  WidgetState,
  type MessageFormatter,
} from '@livekit/components-react';
import { RoomEvent, Track } from 'livekit-client';
import { useStoredMeetingI18n } from './meeting-i18n';

type LocalizedVideoConferenceProps = React.HTMLAttributes<HTMLDivElement> & {
  chatMessageFormatter?: MessageFormatter;
  chatMessageDecoder?: any;
  chatMessageEncoder?: any;
  SettingsComponent?: React.ComponentType;
};

type LocalizedControlBarProps = React.HTMLAttributes<HTMLDivElement> & {
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  controls?: {
    microphone?: boolean;
    camera?: boolean;
    chat?: boolean;
    screenShare?: boolean;
    leave?: boolean;
    settings?: boolean;
  };
  saveUserChoices?: boolean;
};

const trackSourceToProtocol = (source: Track.Source) => {
  switch (source) {
    case Track.Source.Camera:
      return 1;
    case Track.Source.Microphone:
      return 2;
    case Track.Source.ScreenShare:
      return 3;
    default:
      return 0;
  }
};

function sameTrackRef(
  a: TrackReferenceOrPlaceholder | undefined | null,
  b: TrackReferenceOrPlaceholder | undefined | null,
) {
  if (!a || !b) {
    return false;
  }
  const aPublication = isTrackReference(a) ? a.publication : undefined;
  const bPublication = isTrackReference(b) ? b.publication : undefined;
  return (
    a.participant.identity === b.participant.identity &&
    a.source === b.source &&
    aPublication?.trackSid === bPublication?.trackSid
  );
}

function useControlBarVariation(showChat: boolean) {
  const [isNarrow, setIsNarrow] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(`(max-width: ${showChat ? 1000 : 760}px)`);
    const update = () => setIsNarrow(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [showChat]);

  const showIcon = true;
  const showText = !isNarrow;
  return { showIcon, showText };
}

function supportsScreenSharing() {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
}

function SpeakerIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor">
      <path d="M7.2 2.2a.75.75 0 0 1 .3.6v10.4a.75.75 0 0 1-1.2.6L3.7 11.8H1.75A1.75 1.75 0 0 1 0 10.05v-4.1C0 4.98.78 4.2 1.75 4.2H3.7l2.6-2a.75.75 0 0 1 .9 0Z" />
      <path d="M10.08 4.22a.75.75 0 0 1 1.06 0 5.35 5.35 0 0 1 0 7.56.75.75 0 1 1-1.06-1.06 3.85 3.85 0 0 0 0-5.44.75.75 0 0 1 0-1.06Z" />
      <path d="M12.61 2.1a.75.75 0 0 1 1.06 0 8.35 8.35 0 0 1 0 11.8.75.75 0 0 1-1.06-1.06 6.85 6.85 0 0 0 0-9.68.75.75 0 0 1 0-1.06Z" />
    </svg>
  );
}

function SpeakerDeviceMenu({
  showIcon,
  showText,
  label,
}: {
  showIcon: boolean;
  showText: boolean;
  label: string;
}) {
  const room = useRoomContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceId] = React.useState('default');
  const menuGroupRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    const updateDevices = async () => {
      try {
        const nextDevices = (await navigator.mediaDevices.enumerateDevices()).filter(
          (device) => device.kind === 'audiooutput',
        );
        if (!cancelled) {
          setDevices(nextDevices);
          setActiveDeviceId(room.getActiveDevice('audiooutput') ?? 'default');
        }
      } catch (error) {
        room.emit(
          RoomEvent.MediaDevicesError,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!menuGroupRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    void updateDevices();
    navigator.mediaDevices.addEventListener?.('devicechange', updateDevices);
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener?.('devicechange', updateDevices);
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen, room]);

  const selectDevice = React.useCallback(
    async (deviceId: string) => {
      try {
        await room.switchActiveDevice('audiooutput', deviceId, true);
        setActiveDeviceId(deviceId);
        setIsOpen(false);
      } catch (error) {
        room.emit(
          RoomEvent.MediaDevicesError,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    },
    [room],
  );

  return (
    <div className="lk-button-group">
      <span className="lk-button">
        {showIcon && <SpeakerIcon />}
        {showText && label}
      </span>
      <div className="lk-button-group-menu" ref={menuGroupRef}>
        <button
          className="lk-button lk-button-menu"
          type="button"
          aria-label={label}
          aria-expanded={isOpen}
          aria-pressed={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        />
        {isOpen && (
          <div className="lk-device-menu">
            <ul className="lk-list lk-media-device-select" role="listbox">
              {devices.map((device, index) => {
                const isActive =
                  device.deviceId === activeDeviceId ||
                  (activeDeviceId === 'default' &&
                    !devices.some((item) => item.deviceId === 'default') &&
                    index === 0);
                return (
                  <li
                    key={device.deviceId}
                    data-lk-active={isActive}
                    aria-selected={isActive}
                    role="option"
                  >
                    <button
                      className="lk-button"
                      type="button"
                      onClick={() => void selectDevice(device.deviceId)}
                    >
                      {device.label || `${label} ${index + 1}`}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function LocalizedControlBar({
  controls,
  saveUserChoices = true,
  onDeviceError,
  className,
  ...props
}: LocalizedControlBarProps) {
  const t = useStoredMeetingI18n();
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();

  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext.widget.state.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);

  const { showIcon, showText } = useControlBarVariation(isChatOpen);
  const visibleControls = { leave: true, ...controls };
  const localPermissions = useLocalParticipantPermissions();
  const room = useRoomContext();

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
  } else {
    const canPublishSource = (source: Track.Source) =>
      localPermissions.canPublish &&
      (localPermissions.canPublishSources.length === 0 ||
        localPermissions.canPublishSources.includes(trackSourceToProtocol(source)));

    visibleControls.camera ??= canPublishSource(Track.Source.Camera);
    visibleControls.microphone ??= canPublishSource(Track.Source.Microphone);
    visibleControls.screenShare ??= canPublishSource(Track.Source.ScreenShare);
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);
  const handleLeave = React.useCallback(() => {
    room.disconnect(true);
    window.location.replace(window.location.pathname);
  }, [room]);

  return (
    <div className={['lk-control-bar', className].filter(Boolean).join(' ')} {...props}>
      {visibleControls.microphone && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Microphone}
            showIcon={showIcon}
            onChange={(enabled, isUserInitiated) =>
              isUserInitiated ? saveAudioInputEnabled(enabled) : null
            }
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
          >
            {showText && t.microphone}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="audioinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveAudioInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      <SpeakerDeviceMenu showIcon={showIcon} showText={showText} label={t.speakerControl} />
      {visibleControls.camera && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={showIcon}
            onChange={(enabled, isUserInitiated) =>
              isUserInitiated ? saveVideoInputEnabled(enabled) : null
            }
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Camera, error })}
          >
            {showText && t.camera}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {visibleControls.screenShare && supportsScreenSharing() && (
        <TrackToggle
          source={Track.Source.ScreenShare}
          captureOptions={{ audio: true, selfBrowserSurface: 'include' }}
          showIcon={showIcon}
          onChange={setIsScreenShareEnabled}
          onDeviceError={(error) => onDeviceError?.({ source: Track.Source.ScreenShare, error })}
        >
          {showText && (isScreenShareEnabled ? t.stopScreenShare : t.shareScreen)}
        </TrackToggle>
      )}
      {visibleControls.chat && (
        <ChatToggle>
          {showIcon && <ChatIcon />}
          {showText && t.chat}
        </ChatToggle>
      )}
      {visibleControls.settings && (
        <button
          className="lk-button"
          type="button"
          aria-pressed={layoutContext?.widget.state?.showSettings ? 'true' : 'false'}
          onClick={() => layoutContext?.widget.dispatch?.({ msg: 'toggle_settings' })}
        >
          {showIcon && <GearIcon />}
          {showText && t.settings}
        </button>
      )}
      {visibleControls.leave && (
        <button className="lk-disconnect-button" type="button" onClick={handleLeave}>
          {showIcon && <LeaveIcon />}
          {showText && t.leave}
        </button>
      )}
      <StartMediaButton label={t.startMedia} />
    </div>
  );
}

function LocalizedChat({
  messageFormatter,
  messageDecoder,
  messageEncoder,
  channelTopic,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  messageFormatter?: MessageFormatter;
  messageDecoder?: any;
  messageEncoder?: any;
  channelTopic?: string;
}) {
  const t = useStoredMeetingI18n();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const chatOptions = React.useMemo(
    () => ({ messageDecoder, messageEncoder, channelTopic }),
    [messageDecoder, messageEncoder, channelTopic],
  );
  const { chatMessages, send, isSending } = useChat(chatOptions);
  const layoutContext = useMaybeLayoutContext();
  const lastReadMsgAt = React.useRef(0);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const value = inputRef.current?.value.trim();
    if (!value) {
      return;
    }
    await send(value);
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }

  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [chatMessages]);

  React.useEffect(() => {
    if (!layoutContext || chatMessages.length === 0) {
      return;
    }
    const latest = chatMessages[chatMessages.length - 1];
    if (
      layoutContext.widget.state?.showChat &&
      latest &&
      lastReadMsgAt.current !== latest.timestamp
    ) {
      lastReadMsgAt.current = latest.timestamp;
      return;
    }
    const unreadMessageCount = chatMessages.filter(
      (msg) => !lastReadMsgAt.current || msg.timestamp > lastReadMsgAt.current,
    ).length;
    if (
      unreadMessageCount > 0 &&
      layoutContext.widget.state?.unreadMessages !== unreadMessageCount
    ) {
      layoutContext.widget.dispatch?.({ msg: 'unread_msg', count: unreadMessageCount });
    }
  }, [chatMessages, layoutContext]);

  return (
    <div {...props} className="lk-chat">
      <div className="lk-chat-header">
        {t.messages}
        {layoutContext && (
          <ChatToggle className="lk-close-button">
            <ChatCloseIcon />
          </ChatToggle>
        )}
      </div>
      <ul className="lk-list lk-chat-messages" ref={listRef}>
        {chatMessages.map((msg, idx, allMsg) => {
          const previous = allMsg[idx - 1];
          const hideName = idx >= 1 && previous?.from === msg.from;
          const hideTimestamp = idx >= 1 && msg.timestamp - previous.timestamp < 60_000;
          return (
            <ChatEntry
              key={msg.id ?? idx}
              hideName={hideName}
              hideTimestamp={hideName === false ? false : hideTimestamp}
              entry={msg}
              messageFormatter={messageFormatter}
            />
          );
        })}
      </ul>
      <form className="lk-chat-form" onSubmit={handleSubmit}>
        <input
          className="lk-form-control lk-chat-form-input"
          disabled={isSending}
          ref={inputRef}
          type="text"
          placeholder={t.messagePlaceholder}
          onInput={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onKeyUp={(event) => event.stopPropagation()}
        />
        <button type="submit" className="lk-button lk-chat-form-button" disabled={isSending}>
          {t.send}
        </button>
      </form>
    </div>
  );
}

export function LocalizedVideoConference({
  chatMessageFormatter,
  chatMessageDecoder,
  chatMessageEncoder,
  SettingsComponent,
  ...props
}: LocalizedVideoConferenceProps) {
  const [widgetState, setWidgetState] = React.useState<WidgetState>({
    showChat: false,
    unreadMessages: 0,
    showSettings: false,
  });
  const [hasOpenedSettings, setHasOpenedSettings] = React.useState(false);
  const lastAutoFocusedScreenShareTrack = React.useRef<TrackReferenceOrPlaceholder | null>(null);
  const layoutContext = useCreateLayoutContext();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  );

  const screenShareTracks = tracks
    .filter(isTrackReference)
    .filter((track) => track.publication.source === Track.Source.ScreenShare);
  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = tracks.filter((track) => !sameTrackRef(track, focusTrack));
  const screenShareTrackState = screenShareTracks
    .map((track) => `${track.publication.trackSid}_${track.publication.isSubscribed}`)
    .join();
  const focusTrackState = focusTrack
    ? `${focusTrack.participant.identity}_${focusTrack.source}_${
        isTrackReference(focusTrack) ? focusTrack.publication.trackSid : 'placeholder'
      }`
    : '';

  const handleWidgetChange = React.useCallback((state: WidgetState) => {
    setWidgetState(state);
    if (state.showSettings) {
      setHasOpenedSettings(true);
    }
  }, []);

  React.useEffect(() => {
    if (
      screenShareTracks.some((track) => track.publication.isSubscribed) &&
      lastAutoFocusedScreenShareTrack.current === null
    ) {
      layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: screenShareTracks[0] });
      lastAutoFocusedScreenShareTrack.current = screenShareTracks[0];
    } else if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some((track) =>
        sameTrackRef(track, lastAutoFocusedScreenShareTrack.current),
      )
    ) {
      layoutContext.pin.dispatch?.({ msg: 'clear_pin' });
      lastAutoFocusedScreenShareTrack.current = null;
    }

    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (track) =>
          track.participant.identity === focusTrack.participant.identity &&
          track.source === focusTrack.source,
      );
      if (
        updatedFocusTrack &&
        updatedFocusTrack !== focusTrack &&
        isTrackReference(updatedFocusTrack)
      ) {
        layoutContext.pin.dispatch?.({ msg: 'set_pin', trackReference: updatedFocusTrack });
      }
    }
  }, [screenShareTrackState, focusTrackState, tracks, layoutContext.pin.dispatch]);

  return (
    <div className="lk-video-conference" {...props}>
      <LayoutContextProvider value={layoutContext} onWidgetChange={handleWidgetChange}>
        <div className="lk-video-conference-inner">
          {!focusTrack ? (
            <div className="lk-grid-layout-wrapper">
              <GridLayout tracks={tracks}>
                <ParticipantTile />
              </GridLayout>
            </div>
          ) : (
            <div className="lk-focus-layout-wrapper">
              <FocusLayoutContainer>
                <CarouselLayout tracks={carouselTracks}>
                  <ParticipantTile />
                </CarouselLayout>
                {focusTrack && <FocusLayout trackRef={focusTrack} />}
              </FocusLayoutContainer>
            </div>
          )}
          <LocalizedControlBar controls={{ chat: true, settings: !!SettingsComponent }} />
        </div>
        <LocalizedChat
          style={{ display: widgetState.showChat ? 'grid' : 'none' }}
          messageFormatter={chatMessageFormatter}
          messageDecoder={chatMessageDecoder}
          messageEncoder={chatMessageEncoder}
        />
        {SettingsComponent && hasOpenedSettings && (
          <div
            className="lk-settings-menu-modal"
            style={{ display: widgetState.showSettings ? 'block' : 'none' }}
          >
            <SettingsComponent />
          </div>
        )}
      </LayoutContextProvider>
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
